/* =============================================================================
   PlateX dateless format taxonomy — the FINAL, permanent platform convention
   (Dateless build pack §4.1, approved).

   A format is always classified as `letters × numbers`, counting LETTERS
   FIRST regardless of the registration's visible order:

     A 12  -> 1×2        12 A  -> 1×2
     AB 1  -> 2×1        1 AB  -> 2×1        1 BJ -> 2×1

   Arrangement is a SEPARATE property (the visible order): letter-first or
   number-first (reversed). Both arrangements of a format share one register
   group; arrangement is never a format class, route or pill distinction.

   These rules are covered by permanent unit tests (format-token.spec.ts).
   Do not reinterpret them.
   ========================================================================== */

export type Arrangement = 'letter-first' | 'number-first';

export interface DatelessFormat {
  letters: number;
  numbers: number;
  /** Canonical ASCII URL token, lowercase: "2x1". */
  token: string;
  /** Display form with the multiplication sign: "2×1". */
  display: string;
  /** Plain-English definition, e.g. "Two letters and one number". */
  plainEnglish: string;
}

export interface ClassifiedRegistration extends DatelessFormat {
  arrangement: Arrangement;
  arrangementLabel: 'Letter First' | 'Number First (Reversed)';
}

const WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven'];

function word(n: number): string {
  return WORDS[n] || String(n);
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "Two letters and one number" — the screen-reader / adjacent-text form. */
export function plainEnglish(letters: number, numbers: number): string {
  const l = `${word(letters)} letter${letters === 1 ? '' : 's'}`;
  const n = `${word(numbers)} number${numbers === 1 ? '' : 's'}`;
  return capitalise(`${l} and ${n}`);
}

export function buildFormat(letters: number, numbers: number): DatelessFormat {
  return {
    letters,
    numbers,
    token: `${letters}x${numbers}`,
    display: `${letters}×${numbers}`,
    plainEnglish: plainEnglish(letters, numbers),
  };
}

/**
 * Classify a dateless registration string (e.g. "1 BJ", "AB 12", "12A").
 * Returns null when the input is not a two-group dateless shape.
 * LETTERS ARE COUNTED FIRST regardless of visible order: "1 BJ" -> 2×1.
 */
export function classifyRegistration(registration: string): ClassifiedRegistration | null {
  const compact = (registration || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  // At most three letters: no 4-letter dateless mark exists (owner, 2026-10-23).
  const letterFirst = compact.match(/^([A-Z]{1,3})([0-9]{1,4})$/);
  const numberFirst = compact.match(/^([0-9]{1,4})([A-Z]{1,3})$/);
  if (!letterFirst && !numberFirst) return null;
  const letters = letterFirst ? letterFirst[1].length : numberFirst![2].length;
  const numbers = letterFirst ? letterFirst[2].length : numberFirst![1].length;
  const arrangement: Arrangement = letterFirst ? 'letter-first' : 'number-first';
  return {
    ...buildFormat(letters, numbers),
    arrangement,
    arrangementLabel: arrangement === 'letter-first' ? 'Letter First' : 'Number First (Reversed)',
  };
}

/**
 * Parse a URL token ("2x1", case-insensitive; also accepts the display form
 * "2×1"). Returns null for anything malformed.
 */
export function parseFormatToken(token: string): DatelessFormat | null {
  const m = (token || '').toLowerCase().replace('×', 'x').match(/^([1-3])x([1-4])$/);
  if (!m) return null;
  return buildFormat(Number(m[1]), Number(m[2]));
}

/** The Dateless Reference deep-link base — the single route contract every
    surface (plate detail, browse pills, collections) links through.
    Phase 8: points at the public reference route; the dateless-draft route
    remains as the unindexed review fallback. */
export const DATELESS_REFERENCE_PATH = '/plates/dateless';

export function datelessReferenceLink(token: string): string[] {
  return [DATELESS_REFERENCE_PATH, token];
}

/** The browse taxonomy: the format groups the Dateless Reference offers. */
export interface FormatGroup {
  letters: number;
  name: string;
  formats: DatelessFormat[];
}

export const FORMAT_GROUPS: FormatGroup[] = [
  { letters: 1, name: 'Single letter', formats: [1, 2, 3, 4].map((n) => buildFormat(1, n)) },
  { letters: 2, name: 'Two letter', formats: [1, 2, 3, 4].map((n) => buildFormat(2, n)) },
  { letters: 3, name: 'Three letter', formats: [1, 2, 3, 4].map((n) => buildFormat(3, n)) },
];

/** Token -> format for every format the reference recognises. */
export const KNOWN_FORMATS: { [token: string]: DatelessFormat } = FORMAT_GROUPS
  .reduce((acc, g) => {
    g.formats.forEach((f) => (acc[f.token] = f));
    return acc;
  }, {} as { [token: string]: DatelessFormat });
